import React, { createContext, useReducer, useCallback } from "react";
import { SessionState, SessionStateActionType, SessionStateActions } from "../store/sessionActions";
import apiServices from "../services/apiServices";

interface SessionContextValue {
   state: SessionState;
   dispatch: React.Dispatch<SessionStateActions>;
   switchDomain: (domain: string) => Promise<void>;
}

const SessionContext = createContext<SessionContextValue>({
   state: { domain: "", permissions: [] },
   dispatch: () => undefined,
   switchDomain: () => Promise.resolve(),
});
const { Provider } = SessionContext;

const SessionProvider: React.FC<{
   children?: React.ReactNode;
}> = ({ children }) => {
   const [state, dispatch] = useReducer(
      (state: SessionState, action: SessionStateActions): SessionState => {
         switch (action.type) {
            case SessionStateActionType.SetDomain:
               return {
                  ...state,
                  domain: action.domain,
               };
            case SessionStateActionType.SetPermissions:
               return {
                  ...state,
                  permissions: action.permissions,
               };
            default:
               throw new Error();
         }
      },
      { permissions: [] },
   );

   const switchDomain = useCallback(async (domain: string) => {
      dispatch({ type: SessionStateActionType.SetDomain, domain });
      try {
         const permRes = await apiServices.get(`/domain/permissions/${domain}`);
         dispatch({ type: SessionStateActionType.SetPermissions, permissions: permRes.data.permissions || [] });
      } catch {
         dispatch({ type: SessionStateActionType.SetPermissions, permissions: [] });
      }
   }, []);

   return <Provider value={{ state, dispatch, switchDomain }}>{children}</Provider>;
};

export { SessionContext, SessionProvider };
